import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PasscodeContext = createContext(null);

// Storage keys
const PASSCODE_KEY = 'hj-passcode';
const PASSCODE_ENABLED_KEY = 'hj-passcode-enabled';
const PASSCODE_LENGTH_KEY = 'hj-passcode-length';
const FILE_PICKER_ACTIVE_KEY = 'hj-file-picker-active';

// Check if running in Capacitor native app
const isNativePlatform = () => window.Capacitor?.isNativePlatform?.() || false;

// Helper to mark file picker as active (call before opening file picker)
export const markFilePickerActive = () => {
  sessionStorage.setItem(FILE_PICKER_ACTIVE_KEY, 'true');
};

// Helper to clear file picker active state
export const clearFilePickerActive = () => {
  sessionStorage.removeItem(FILE_PICKER_ACTIVE_KEY);
};

// Check if file picker is active
const isFilePickerActive = () => sessionStorage.getItem(FILE_PICKER_ACTIVE_KEY) === 'true';

export function PasscodeProvider({ children }) {
  const [isPasscodeSet, setIsPasscodeSet] = useState(false);
  const [isPasscodeEnabled, setIsPasscodeEnabled] = useState(false);
  const [passcodeLength, setPasscodeLengthState] = useState(4); // 4 or 6 digits
  const [isLocked, setIsLocked] = useState(false);
  const [isDesktopLocked, setIsDesktopLocked] = useState(false);

  // Initialize state from localStorage
  useEffect(() => {
    const storedPasscode = localStorage.getItem(PASSCODE_KEY);
    const storedEnabled = localStorage.getItem(PASSCODE_ENABLED_KEY);
    const storedLength = localStorage.getItem(PASSCODE_LENGTH_KEY);

    setIsPasscodeSet(!!storedPasscode);
    setIsPasscodeEnabled(storedEnabled === 'true');
    if (storedLength) {
      setPasscodeLengthState(parseInt(storedLength, 10));
    }

    // On mobile, lock app if passcode is enabled
    if (isNativePlatform() && storedPasscode && storedEnabled === 'true') {
      setIsLocked(true);
    }
  }, []);

  // Handle app visibility change (for mobile - lock when app goes to background)
  useEffect(() => {
    if (!isNativePlatform()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // App went to background - will lock when it comes back
        // Skip if file picker is active (user is selecting a file)
        if (isPasscodeEnabled && isPasscodeSet && !isFilePickerActive()) {
          // Mark that we need to lock
          sessionStorage.setItem('hj-should-lock', 'true');
        }
      } else if (document.visibilityState === 'visible') {
        // App came back to foreground
        // If file picker was active, clear it and skip locking
        if (isFilePickerActive()) {
          clearFilePickerActive();
          sessionStorage.removeItem('hj-should-lock');
          return;
        }

        const shouldLock = sessionStorage.getItem('hj-should-lock');
        if (shouldLock === 'true' && isPasscodeEnabled && isPasscodeSet) {
          setIsLocked(true);
          sessionStorage.removeItem('hj-should-lock');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPasscodeEnabled, isPasscodeSet]);

  // Set a new passcode
  const setPasscode = useCallback((passcode) => {
    localStorage.setItem(PASSCODE_KEY, passcode);
    localStorage.setItem(PASSCODE_LENGTH_KEY, passcode.length.toString());
    setIsPasscodeSet(true);
    setPasscodeLengthState(passcode.length);
  }, []);

  // Enable/disable passcode
  const togglePasscodeEnabled = useCallback((enabled) => {
    localStorage.setItem(PASSCODE_ENABLED_KEY, enabled.toString());
    setIsPasscodeEnabled(enabled);
    if (!enabled) {
      setIsLocked(false);
      setIsDesktopLocked(false);
    }
  }, []);

  // Set passcode length (4 or 6)
  const setPasscodeLength = useCallback((length) => {
    localStorage.setItem(PASSCODE_LENGTH_KEY, length.toString());
    setPasscodeLengthState(length);
  }, []);

  // Verify passcode
  const verifyPasscode = useCallback((inputPasscode) => {
    const storedPasscode = localStorage.getItem(PASSCODE_KEY);
    return inputPasscode === storedPasscode;
  }, []);

  // Unlock the app (mobile)
  const unlock = useCallback(() => {
    setIsLocked(false);
  }, []);

  // Lock/unlock desktop
  const lockDesktop = useCallback(() => {
    if (isPasscodeEnabled && isPasscodeSet) {
      setIsDesktopLocked(true);
    }
  }, [isPasscodeEnabled, isPasscodeSet]);

  const unlockDesktop = useCallback(() => {
    setIsDesktopLocked(false);
  }, []);

  // Reset passcode (requires current passcode verification done before calling)
  const resetPasscode = useCallback((newPasscode) => {
    localStorage.setItem(PASSCODE_KEY, newPasscode);
    localStorage.setItem(PASSCODE_LENGTH_KEY, newPasscode.length.toString());
    setPasscodeLengthState(newPasscode.length);
  }, []);

  // Clear passcode completely
  const clearPasscode = useCallback(() => {
    localStorage.removeItem(PASSCODE_KEY);
    localStorage.removeItem(PASSCODE_ENABLED_KEY);
    localStorage.removeItem(PASSCODE_LENGTH_KEY);
    setIsPasscodeSet(false);
    setIsPasscodeEnabled(false);
    setIsLocked(false);
    setIsDesktopLocked(false);
  }, []);

  const value = {
    isPasscodeSet,
    isPasscodeEnabled,
    passcodeLength,
    isLocked,
    isDesktopLocked,
    setPasscode,
    togglePasscodeEnabled,
    setPasscodeLength,
    verifyPasscode,
    unlock,
    lockDesktop,
    unlockDesktop,
    resetPasscode,
    clearPasscode,
    isNativePlatform: isNativePlatform()
  };

  return (
    <PasscodeContext.Provider value={value}>
      {children}
    </PasscodeContext.Provider>
  );
}

export function usePasscode() {
  const context = useContext(PasscodeContext);
  if (!context) {
    throw new Error('usePasscode must be used within a PasscodeProvider');
  }
  return context;
}
